import Axios from 'axios';
import { cloneDeep } from 'lodash-es';
import EnvironmentService from '@/env/EnvironmentService';
import processProxy from '@/env/processProxy';
import { FpAxios } from '@/services/FpAxios';

type SimulationPdfDto = {
  url: string;
}

class SimulationApi {
  // The simulation backend is a separate, tokenless service, so this is a plain axios instance, not FpAxios.
  // Both product services compose this one instance, so the app-hash header is configured in a single place.
  public readonly client = Axios.create({
    headers: {
      ...(!EnvironmentService.Environment.isDev && {'X-App-Hash': processProxy.VUE_APP_COMMIT_HASH,}),
    },
  });

  // The pdf endpoints are called without the app-hash header, exactly as the current comparator calls them.
  private pdfAxios = Axios.create();

  private dealsAxios = FpAxios.create();

  // processProxy resolves `{PLACEHOLDER}` against window on every read, so urls are built per request, never at import time.
  public get baseUrl(): string {
    return `${processProxy.VUE_APP_SIMULATION_URL}`;
  }

  // The backend chokes on an empty `households` array — the key has to be absent instead.
  // The deep copy freezes the body now: axios 0.18 serializes it a microtask later, after Vue may have mutated the store.
  public withoutEmptyHouseholds<TUserInput extends {households?: unknown[];}>(userInput: TUserInput): TUserInput {
    const data = cloneDeep(userInput);
    if (data.households && data.households.length === 0) {
      delete data.households;
    }
    return data;
  }

  public async generatePdf(url: string, filename: string): Promise<SimulationPdfDto> {
    const response = await this.pdfAxios.post<SimulationPdfDto>(`${this.baseUrl}pdf`, {url, filename,});
    return response.data;
  }

  public async generatePdfBatch(urls: string[], filename: string): Promise<SimulationPdfDto> {
    const response = await this.pdfAxios.post<SimulationPdfDto>(`${this.baseUrl}pdf/batch`, {urls, filename,});
    return response.data;
  }

  // A deals endpoint, not a simulation one: the same url for both products, only the payload is product-typed,
  // so each product service exposes its own typed wrapper over this call.
  public async addOffersToDeal<TUserInput extends {households?: unknown[];}, TOffer>(
    dealId: number,
    simulationId: string,
    userInput: TUserInput,
    offers: TOffer[],
  ): Promise<void> {
    await this.dealsAxios.post(`deals/${dealId}/applications`, {
      simulationInput: this.withoutEmptyHouseholds(userInput),
      simulationId,
      offers,
    });
  }
}

export default new SimulationApi();
